import { MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import CompactForm, { FormField } from '../ui/Form/CompactForm';
import FormFieldGroup from '../ui/Form/FormFieldGroup';
import ResponsiveModal from '../ui/Modal/ResponsiveModal';
import CollapsibleSection from '../ui/Section/CollapsibleSection';
import PhoneInput from '../ui/PhoneInput';
import { apiService } from '../../services/api';
import { parseStreetAddress } from '../../utils/addressUtils';
import { formatPhone } from '../../utils/formatPhone';

interface Customer {
  id?: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  date_of_birth?: string;
  ssn_last_four?: string;
  phone: string;
  email: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  employer?: string;
  job_title?: string;
  work_phone?: string;
  monthly_income?: number;
  employment_start_date?: string;
  credit_score?: number;
  credit_tier?: string;
  status: string;
  preferred_contact_method?: string;
  lead_source?: string;
  notes?: string;
  tags?: string[];
}

interface InterestedVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  asking_price: number;
  priority_order: number;
  notes?: string;
  primary_image?: string;
}

interface CustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (customer: Customer) => Promise<Customer | void>;
  customer?: Customer | null;
  loading?: boolean;
}

const CustomerModal: React.FC<CustomerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  customer,
  loading = false,
}) => {
  // Status transformation functions
  const backendToFrontendStatus = (backendStatus: string): string => {
    const mapping: Record<string, string> = {
      active: 'ACTIVE',
      inactive: 'INACTIVE',
      do_not_call: 'DO_NOT_CALL',
    };
    return mapping[backendStatus] || backendStatus.toUpperCase();
  };

  const frontendToBackendStatus = (frontendStatus: string): string => {
    const mapping: Record<string, string> = {
      ACTIVE: 'active',
      INACTIVE: 'inactive',
      DO_NOT_CALL: 'do_not_call',
    };
    return mapping[frontendStatus] || frontendStatus.toLowerCase();
  };
  const [formData, setFormData] = useState<Customer>({
    first_name: '',
    last_name: '',
    middle_name: '',
    date_of_birth: '',
    ssn_last_four: '',
    phone: '',
    email: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    employer: '',
    job_title: '',
    work_phone: '',
    monthly_income: undefined,
    employment_start_date: '',
    credit_score: undefined,
    credit_tier: '',
    status: 'ACTIVE',
    preferred_contact_method: 'phone',
    lead_source: '',
    notes: '',
    tags: [],
  });

  const [interestedVehicles, setInterestedVehicles] = useState<InterestedVehicle[]>([]);
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');
  const [availableVehicles, setAvailableVehicles] = useState<any[]>([]);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (customer) {
      // Parse address components if needed
      const parsedAddress = parseStreetAddress(customer.street_address);

      setFormData({
        ...customer,
        phone: formatPhone(customer.phone || ''),
        work_phone: formatPhone(customer.work_phone || ''),
        street_address: parsedAddress.street_address || customer.street_address || '',
        city: parsedAddress.city || customer.city || '',
        state: parsedAddress.state || customer.state || '',
        zip_code: parsedAddress.zip_code || customer.zip_code || '',
        status: backendToFrontendStatus(customer.status),
        tags: customer.tags || [],
      });
      // Initialize interested vehicles if customer has them
      if ((customer as any).interested_vehicles) {
        setInterestedVehicles((customer as any).interested_vehicles);
      } else {
        setInterestedVehicles([]);
      }
    } else {
      // Reset form for new customer
      setFormData({
        first_name: '',
        last_name: '',
        middle_name: '',
        date_of_birth: '',
        ssn_last_four: '',
        phone: '',
        email: '',
        street_address: '',
        city: '',
        state: '',
        zip_code: '',
        employer: '',
        job_title: '',
        work_phone: '',
        monthly_income: undefined,
        employment_start_date: '',
        credit_score: undefined,
        credit_tier: '',
        status: 'ACTIVE',
        preferred_contact_method: 'phone',
        lead_source: '',
        notes: '',
        tags: [],
      });
      setInterestedVehicles([]);
    }
    setErrors({});
  }, [customer, isOpen]);

  // Fetch available vehicles when modal opens
  useEffect(() => {
    const fetchVehicles = async () => {
      if (isOpen) {
        try {
          const vehicles = await apiService.get<any[]>('/vehicles/');
          setAvailableVehicles(vehicles);
        } catch (error) {
          console.error('Error fetching vehicles:', error);
        }
      }
    };
    fetchVehicles();
  }, [isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email format is invalid';
    }

    if (
      formData.ssn_last_four &&
      (formData.ssn_last_four.length !== 4 || !/^\d{4}$/.test(formData.ssn_last_four))
    ) {
      newErrors.ssn_last_four = 'SSN last 4 must be exactly 4 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Clean and transform data to backend format
      const cleanData = { ...formData };

      // Convert empty strings to null for optional numeric fields
      if (
        cleanData.monthly_income === undefined ||
        cleanData.monthly_income === null ||
        (cleanData.monthly_income as any) === ''
      ) {
        cleanData.monthly_income = undefined;
      }
      if (
        cleanData.credit_score === undefined ||
        cleanData.credit_score === null ||
        (cleanData.credit_score as any) === ''
      ) {
        cleanData.credit_score = undefined;
      }

      // Convert empty strings to null for optional date fields
      if (!cleanData.date_of_birth || cleanData.date_of_birth === '') {
        cleanData.date_of_birth = undefined;
      }
      if (!cleanData.employment_start_date || cleanData.employment_start_date === '') {
        cleanData.employment_start_date = undefined;
      }

      // Convert empty strings to null for optional string fields
      if (!cleanData.middle_name || cleanData.middle_name === '') {
        cleanData.middle_name = undefined;
      }
      if (!cleanData.ssn_last_four || cleanData.ssn_last_four === '') {
        cleanData.ssn_last_four = undefined;
      }
      if (!cleanData.credit_tier || cleanData.credit_tier === '') {
        cleanData.credit_tier = undefined;
      }
      if (!cleanData.employer || cleanData.employer === '') {
        cleanData.employer = undefined;
      }
      if (!cleanData.job_title || cleanData.job_title === '') {
        cleanData.job_title = undefined;
      }
      if (!cleanData.work_phone || cleanData.work_phone === '') {
        cleanData.work_phone = undefined;
      }
      if (!cleanData.lead_source || cleanData.lead_source === '') {
        cleanData.lead_source = undefined;
      }
      if (!cleanData.notes || cleanData.notes === '') {
        cleanData.notes = undefined;
      }

      const dataToSave = {
        ...cleanData,
        status: frontendToBackendStatus(cleanData.status),
      };

      // Save the customer first and get the returned customer object
      const savedCustomer = await onSave(dataToSave);

      // Get customer ID from either existing customer or newly created one
      const customerId = customer?.id || (savedCustomer as any)?.id;

      // If we have a customer ID, sync interested vehicles
      if (customerId) {
        // Get current interested vehicles from the original customer
        const currentVehicles = (customer as any).interested_vehicles || [];

        // Remove vehicles that are no longer interested
        for (const currentVehicle of currentVehicles) {
          if (!interestedVehicles.some((iv) => iv.id === currentVehicle.id)) {
            try {
              await apiService.removeInterestedVehicle(customerId, currentVehicle.id);
            } catch (error) {
              console.error('Error removing interested vehicle:', error);
            }
          }
        }

        // Add or update interested vehicles
        for (const vehicle of interestedVehicles) {
          const isExisting = currentVehicles.some((cv: any) => cv.id === vehicle.id);
          try {
            if (isExisting) {
              // Update existing vehicle interest
              await apiService.updateInterestedVehicle(customerId, vehicle.id, {
                priority_order: vehicle.priority_order,
                notes: vehicle.notes || '',
              });
            } else {
              // Add new vehicle interest
              await apiService.addInterestedVehicle(
                customerId,
                vehicle.id,
                vehicle.priority_order,
                vehicle.notes || ''
              );
            }
          } catch (error) {
            console.error('Error syncing interested vehicle:', error);
          }
        }
      }

      onClose();
    } catch (error) {
      console.error('Error saving customer:', error);
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={customer ? 'Edit Customer' : 'Add New Customer'}
      className="modal-scroll"
    >
      <CompactForm onSubmit={handleSubmit} layout="vertical" gap="sm" responsive={true}>
        {/* Personal Information - Always Expanded */}
        <FormFieldGroup title="Personal Information" defaultExpanded={true}>
          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="First Name" required error={errors.first_name}>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className={`form-input ${errors.first_name ? 'border-red-300' : ''}`}
                required
              />
            </FormField>

            <FormField label="Middle Name">
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name || ''}
                onChange={handleInputChange}
                className="form-input"
              />
            </FormField>

            <FormField label="Last Name" required error={errors.last_name}>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className={`form-input ${errors.last_name ? 'border-red-300' : ''}`}
                required
              />
            </FormField>
          </FormFieldGroup>

          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Date of Birth">
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth || ''}
                onChange={handleInputChange}
                className="form-input"
              />
            </FormField>

            <FormField label="SSN Last 4" error={errors.ssn_last_four}>
              <input
                type="text"
                name="ssn_last_four"
                value={formData.ssn_last_four || ''}
                onChange={handleInputChange}
                className={`form-input ${errors.ssn_last_four ? 'border-red-300' : ''}`}
                placeholder="1234"
                maxLength={4}
              />
            </FormField>

            <FormField label="Status">
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="DO_NOT_CALL">Do Not Call</option>
              </select>
            </FormField>
          </FormFieldGroup>
        </FormFieldGroup>

        {/* Contact Information - Always Expanded */}
        <FormFieldGroup title="Contact Information" defaultExpanded={true}>
          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Phone" required error={errors.phone}>
              <PhoneInput
                name="phone"
                value={formData.phone}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, phone: v }));
                  if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
                }}
                placeholder="(555) 123-4567"
                className={`form-input ${errors.phone ? 'border-red-300' : ''}`}
                required
              />
            </FormField>

            <FormField label="Email" required error={errors.email}>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${errors.email ? 'border-red-300' : ''}`}
                required
              />
            </FormField>

            <FormField label="Work Phone">
              <PhoneInput
                name="work_phone"
                value={formData.work_phone || ''}
                onChange={(v) => setFormData((prev) => ({ ...prev, work_phone: v }))}
                placeholder="(555) 123-4567"
                className="form-input"
              />
            </FormField>
          </FormFieldGroup>

          <FormField label="Preferred Contact Method">
            <select
              name="preferred_contact_method"
              value={formData.preferred_contact_method || 'phone'}
              onChange={handleInputChange}
              className="form-input"
            >
              <option value="phone">Phone</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </FormField>
        </FormFieldGroup>

        {/* Address - Collapsible, Expanded by Default */}
        <CollapsibleSection title="Address" defaultExpanded={true} variant="bordered">
          <FormField label="Street Address">
            <input
              type="text"
              name="street_address"
              value={formData.street_address || ''}
              onChange={handleInputChange}
              className="form-input"
            />
          </FormField>

          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="City">
              <input
                type="text"
                name="city"
                value={formData.city || ''}
                onChange={handleInputChange}
                className="form-input"
              />
            </FormField>

            <FormField label="State">
              <input
                type="text"
                name="state"
                value={formData.state || ''}
                onChange={handleInputChange}
                className="form-input"
                maxLength={2}
              />
            </FormField>

            <FormField label="ZIP Code">
              <input
                type="text"
                name="zip_code"
                value={formData.zip_code || ''}
                onChange={handleInputChange}
                className="form-input"
              />
            </FormField>
          </FormFieldGroup>
        </CollapsibleSection>

        {/* Employment - Collapsible, Expanded for Better UX */}
        <CollapsibleSection
          title="Employment Information"
          defaultExpanded={true}
          variant="bordered"
          subtitle="Employment details and income information"
        >
          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Employer">
              <input
                type="text"
                name="employer"
                value={formData.employer || ''}
                onChange={handleInputChange}
                className="form-input"
              />
            </FormField>

            <FormField label="Job Title">
              <input
                type="text"
                name="job_title"
                value={formData.job_title || ''}
                onChange={handleInputChange}
                className="form-input"
              />
            </FormField>
          </FormFieldGroup>

          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Monthly Income">
              <input
                type="number"
                name="monthly_income"
                value={formData.monthly_income || ''}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
              />
            </FormField>

            <FormField label="Employment Start Date">
              <input
                type="date"
                name="employment_start_date"
                value={formData.employment_start_date || ''}
                onChange={handleInputChange}
                className="form-input"
              />
            </FormField>
          </FormFieldGroup>
        </CollapsibleSection>

        {/* Additional Information - Collapsible, Expanded for Better UX */}
        <CollapsibleSection
          title="Additional Information"
          defaultExpanded={true}
          variant="bordered"
          subtitle="Credit information and lead details"
        >
          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Credit Score">
              <input
                type="number"
                name="credit_score"
                value={formData.credit_score || ''}
                onChange={handleInputChange}
                className="form-input"
                min="300"
                max="850"
              />
            </FormField>

            <FormField label="Lead Source">
              <select
                name="lead_source"
                value={formData.lead_source || ''}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="">Select lead source...</option>
                <option value="Website">Website</option>
                <option value="Referral">Referral</option>
                <option value="Walk-in">Walk-in</option>
                <option value="Phone Call">Phone Call</option>
                <option value="Social Media">Social Media</option>
                <option value="Advertisement">Advertisement</option>
                <option value="Trade Show">Trade Show</option>
                <option value="Other">Other</option>
              </select>
            </FormField>
          </FormFieldGroup>

          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Credit Tier">
              <select
                name="credit_tier"
                value={formData.credit_tier || ''}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="">Select tier...</option>
                <option value="Prime">Prime (740+)</option>
                <option value="Near Prime">Near Prime (640-739)</option>
                <option value="Subprime">Subprime (580-639)</option>
                <option value="Deep Subprime">Deep Subprime (&lt;580)</option>
              </select>
            </FormField>

            <FormField label="Work Phone">
              <PhoneInput
                name="work_phone"
                value={formData.work_phone || ''}
                onChange={(v) => setFormData((prev) => ({ ...prev, work_phone: v }))}
                className="form-input"
                placeholder="(555) 123-4567"
              />
            </FormField>
          </FormFieldGroup>

          <FormField label="Notes">
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={3}
              className="form-input"
              placeholder="Additional notes about the customer..."
            />
          </FormField>
        </CollapsibleSection>

        {/* Interested Vehicles - Collapsible, Expanded for Better UX */}
        <CollapsibleSection
          title="Interested Vehicles"
          defaultExpanded={true}
          variant="bordered"
          subtitle="Vehicle preferences and interests"
          badge={interestedVehicles.length}
        >
          {/* Vehicle Search */}
          <div className="mb-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search vehicles..."
                value={vehicleSearchTerm}
                onChange={(e) => setVehicleSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </div>

          {/* Selected Interested Vehicles */}
          {interestedVehicles.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Vehicles:</h4>
              <div className="space-y-2">
                {interestedVehicles.map((vehicle, index) => (
                  <div
                    key={vehicle.id}
                    className="flex items-center justify-between bg-gray-50 p-3 rounded-md"
                  >
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </span>
                      {vehicle.asking_price && (
                        <span className="ml-2 text-sm text-green-600">
                          ${vehicle.asking_price.toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        placeholder="Priority"
                        value={vehicle.priority_order || ''}
                        onChange={(e) => {
                          const updated = [...interestedVehicles];
                          updated[index].priority_order = parseInt(e.target.value) || 0;
                          setInterestedVehicles(updated);
                        }}
                        className="w-16 form-input text-xs"
                        min="1"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setInterestedVehicles(interestedVehicles.filter((_, i) => i !== index));
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Vehicle Dropdown */}
          <div className="space-y-3">
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => setShowVehicleDropdown(!showVehicleDropdown)}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Interested Vehicle
            </button>

            {showVehicleDropdown && (
              <div className="bg-gray-50 p-4 rounded-lg border">
                <div className="space-y-3">
                  <div>
                    <input
                      type="text"
                      placeholder="Search vehicles..."
                      value={vehicleSearchTerm}
                      onChange={(e) => setVehicleSearchTerm(e.target.value)}
                      className="form-input"
                    />
                  </div>

                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {availableVehicles
                      .filter((vehicle) => {
                        const searchLower = vehicleSearchTerm.toLowerCase();
                        return (
                          vehicle.year.toString().includes(searchLower) ||
                          vehicle.make.toLowerCase().includes(searchLower) ||
                          vehicle.model.toLowerCase().includes(searchLower) ||
                          vehicle.vin?.toLowerCase().includes(searchLower)
                        );
                      })
                      .filter((vehicle) => !interestedVehicles.some((iv) => iv.id === vehicle.id))
                      .slice(0, 10)
                      .map((vehicle) => (
                        <div
                          key={vehicle.id}
                          className="flex items-center justify-between p-2 hover:bg-white rounded cursor-pointer"
                          onClick={() => {
                            const newVehicle: InterestedVehicle = {
                              id: vehicle.id,
                              year: vehicle.year,
                              make: vehicle.make,
                              model: vehicle.model,
                              asking_price: vehicle.asking_price,
                              priority_order: interestedVehicles.length + 1,
                              notes: '',
                              primary_image: vehicle.images?.[0]?.file_path,
                            };
                            setInterestedVehicles([...interestedVehicles, newVehicle]);
                            setShowVehicleDropdown(false);
                            setVehicleSearchTerm('');
                          }}
                        >
                          <div>
                            <div className="text-sm font-medium">
                              {vehicle.year} {vehicle.make} {vehicle.model}
                            </div>
                            <div className="text-xs text-gray-500">
                              {vehicle.vin} - ${vehicle.asking_price?.toLocaleString() || 'N/A'}
                            </div>
                          </div>
                          <PlusIcon className="h-4 w-4 text-blue-600" />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : customer ? 'Update Customer' : 'Create Customer'}
          </button>
        </div>
      </CompactForm>
    </ResponsiveModal>
  );
};

export default CustomerModal;
